import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from '../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { warrantiesInfo } from '../../../../../core/projects.selectors';
import { FetchWarrantiesRequest } from '../../../../../core/projects.actions';
import { ProjectsService } from '../../../../../core/projects.service';
import { AuthService } from '../../../../../../../shared/services';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { AppHttpService } from '../../../../../../../shared/services/app-http.service';
import { WarrantyTasksComponent } from './warranty-tasks.component';
import { computeWarrantyState, WARRANTY_STATES, WarrantyState } from './warranty-state.util';

/**
 * "Tasks" sub-tab under Warranties & Claims: every warranty's milestones and
 * their tasks, listed in the main content area. All CRUD happens in the right
 * panel (WarrantyTasksComponent) — "+ Task" opens it in create mode, clicking
 * a task opens it in detail mode.
 */
@Component({
  selector: 'app-warranty-tasks-list',
  templateUrl: './warranty-tasks-list.component.html',
  styleUrls: ['./warranty-tasks-list.component.scss'],
  standalone: false,
})
export class WarrantyTasksListComponent implements OnInit, OnDestroy {
  projectId: string;
  warranties: any[] = [];
  /** warranty uuid -> tasks[] */
  tasksByWarranty: Record<string, any[]> = {};
  loading = false;
  selectedTaskId: string = null;
  role = '';
  currentUserUuid: string = null;

  states: WarrantyState[] = WARRANTY_STATES;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private projectsService: ProjectsService,
    private authService: AuthService,
    private commonService: CommonService,
    private appHttpService: AppHttpService,
    private cdRef: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    const url = this.router.url;
    const segments = url.split('?')[0].split('/');
    this.projectId = this.route.snapshot.paramMap.get('projectId')
      || segments[segments.indexOf('projects') + 1]
      || '';

    const account = this.authService.getCurrentUser();
    this.currentUserUuid = (account as any)?.uuid || account?.id || null;
    this.role = account?.roles?.find(r => r.project_id === this.projectId)?.role
      || account?.role
      || '';

    this.store.dispatch(FetchWarrantiesRequest({ payload: { projectId: this.projectId } }));
    this.store.select(warrantiesInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(info => {
        if (info?.list) {
          this.warranties = (info.list || []).map(w => ({ ...w, ...(w.attributes || {}) }));
          this.loadTasks();
        }
      });

    // Refresh when the right panel (task CRUD) closes
    this.commonService.clearRowSelection
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.selectedTaskId = null;
        this.loadTasks();
      });
  }

  canManage(): boolean {
    return ['project-manager', 'org-admin', 'inspector', 'lead-inspector', 'contractor-inspector']
      .includes(this.role);
  }

  loadTasks() {
    if (!this.warranties.length) {
      this.tasksByWarranty = {};
      this.loading = false;
      return;
    }
    this.loading = true;
    // Snapshot: `this.warranties` can be replaced by a store emission while the
    // requests are in flight, which would mis-key responses by index.
    const warranties = this.warranties;
    const requests = warranties.map(w =>
      this.appHttpService.getService(
        `/v1/warranty/project/${this.projectId}/warranties/${w.uuid || w.id}/tasks`,
      ).pipe(catchError(() => of({ data: [] }))),
    );
    forkJoin(requests)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((responses: any[]) => {
        const byWarranty: Record<string, any[]> = {};
        responses.forEach((resp, i) => {
          const w = warranties[i];
          byWarranty[w.uuid || w.id] = (resp?.data || []).map(t => ({ ...t, ...(t.attributes || {}) }));
        });
        this.tasksByWarranty = byWarranty;
        this.loading = false;
        this.cdRef.detectChanges();
      });
  }

  currentStateKey(warranty: any): string {
    return computeWarrantyState(warranty)?.key || null;
  }

  tasksFor(warranty: any, milestoneKey: string): any[] {
    const list = this.tasksByWarranty[warranty.uuid || warranty.id] || [];
    return list.filter(t => t.milestone_key === milestoneKey);
  }

  openTaskCount(warranty: any, milestoneKey: string): number {
    return this.tasksFor(warranty, milestoneKey).filter(t => t.status === 'open').length;
  }

  /** Open task assigned to the signed-in user — highlighted in the list. */
  isMyPendingTask(task: any): boolean {
    if (task?.status !== 'open' || !this.currentUserUuid) return false;
    const a = task?.assignedTo;
    return !!a && (a.uuid === this.currentUserUuid || a.id === this.currentUserUuid);
  }

  assigneeName(task: any): string {
    const a = task?.assignedTo;
    if (!a) return 'Unassigned';
    return `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email || 'Unassigned';
  }

  // Menu-item handlers must NOT stopPropagation — it prevents the mat-menu
  // from closing and its transparent backdrop then swallows all clicks.
  addTask(warranty: any, milestoneKey: string) {
    this.selectedTaskId = null;
    this.projectsService.openRightPanel.next({
      component: WarrantyTasksComponent,
      data: { projectId: this.projectId, warranty, milestoneKey },
    });
  }

  deleteTask(warranty: any, task: any) {
    const message = `Deleting <b>${task.title}</b> cannot be undone.<br />Proceed to delete?`;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel('Delete task?', message),
    });
    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      this.appHttpService.deleteService(
        `/v1/warranty/project/${this.projectId}/warranties/${warranty.uuid || warranty.id}/tasks/${task.uuid || task.id}`,
      ).subscribe({
        next: () => {
          this.commonService.notification('Task deleted.', 'success');
          this.loadTasks();
        },
        error: () => this.commonService.notification('Failed to delete task.', 'error'),
      });
    });
  }

  taskClicked(warranty: any, task: any) {
    this.selectedTaskId = task.uuid || task.id;
    this.projectsService.openRightPanel.next({
      component: WarrantyTasksComponent,
      data: { projectId: this.projectId, warranty, task },
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
